/* eslint-disable i18next/no-literal-string */
// src/features/softwareLicenses/pages/LicensesListPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// Hooks
import { useSoftwareLicenses } from '../hooks/useSoftwareLicenses';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';

// Utils
import { UUIDDisplay } from '../../../utils/formatters/uuid';

import PageHeader from '../../../components/common/PageHeader';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function LicensesListPage() {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // API hooks
  const {
    licenses,
    statistics,
    loading,
    error,
    clearError,
    fetchAllLicenses,
    fetchUnactivatedLicenses,
    fetchStatistics,
    deleteLicense,
  } = useSoftwareLicenses();

  // Load initial data
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Load licenses based on active tab
  useEffect(() => {
    if (activeTab === 0) {
      fetchAllLicenses();
    } else if (activeTab === 1) {
      fetchUnactivatedLicenses();
    }
  }, [activeTab, fetchAllLicenses, fetchUnactivatedLicenses]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    if (activeTab === 0) {
      fetchAllLicenses();
    } else {
      fetchUnactivatedLicenses();
    }
    fetchStatistics();
  }, [activeTab, fetchAllLicenses, fetchUnactivatedLicenses, fetchStatistics]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleViewLicense = useCallback((license) => {
    navigate(`/software-licenses/${license.license_id}`);
  }, [navigate]);

  const handleMenuOpen = useCallback((event, license) => {
    event.stopPropagation();
    setSelectedLicense(license);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedLicense(null);
  }, []);

  const handleDeleteLicense = useCallback((license) => {
    showConfirmDialog({
      title: 'Delete License',
      message: `Are you sure you want to delete this license? HWID: ${license.hwid}`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteLicense(license.license_id);
          if (result.success) {
            showSuccess('License deleted');
            handleRefresh();
          } else {
            showError(result.error || 'Failed to delete license');
          }
        } catch (error) {
          showError('Failed to delete license');
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteLicense, showSuccess, showError, handleMenuClose, handleRefresh]);

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'hwid',
      headerName: 'Hardware ID',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: <UUIDDisplay uuid={params.row.license_id} />
          </Typography>
        </Box>
      ),
    },
    {
      field: 'software_package_id',
      headerName: 'Software Package',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'department_name',
      headerName: 'Department',
      width: 150,
      renderCell: (params) => (
        params.value || <Typography variant="body2" color="text.disabled">Not Assigned</Typography>
      ),
    },
    {
      field: 'is_activated',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <CheckCircleIcon /> : <BlockIcon />}
          label={params.value ? 'Activated' : 'Pending'}
          size="small"
          color={params.value ? 'success' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'ip_address',
      headerName: 'IP Address',
      width: 140,
    },
    {
      field: 'last_accessed_at',
      headerName: 'Last Accessed',
      width: 160,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value 
            ? format(new Date(params.value), 'MMM d, yyyy HH:mm')
            : 'Never'
          }
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {format(new Date(params.value), 'MMM d, yyyy HH:mm')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      // Icon-only kebab column: no header, like the other list grids
      headerName: '',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [handleMenuOpen]);

  console.log("LICENSE", licenses)
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <PageHeader
        icon={ListAltOutlinedIcon}
        title={'Software Licenses'}
        subtitle={'Manage software license activations'}
        actions={<>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="license tabs"
        >
          <Tab label="All Licenses" />
          <Tab 
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Pending Activation
                {statistics && statistics.unactivated_licenses > 0 && (
                  <Chip 
                    label={statistics.unactivated_licenses} 
                    size="small" 
                    color="warning" 
                  />
                )}
              </Box>
            } 
          />
        </Tabs>
      </Paper>

      {/* DataGrid */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
          <DataGrid
            rows={licenses}
            columns={columns}
            getRowId={(row) => row.license_id}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnMenu
            onRowClick={(params) => handleViewLicense(params.row)}
            rowHeight={72}
            getRowHeight={() => 'auto'}
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          />
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
          <DataGrid
            rows={licenses}
            columns={columns}
            getRowId={(row) => row.license_id}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnMenu
            onRowClick={(params) => handleViewLicense(params.row)}
            rowHeight={72}
            getRowHeight={() => 'auto'}
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          />
        </Box>
      </TabPanel>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
        }}
      >
        <MenuItem onClick={() => { handleViewLicense(selectedLicense); handleMenuClose(); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => { handleDeleteLicense(selectedLicense); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography color="error">Delete</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Box>
  );
}